/**
 * CompraInteligenteDashboardPage
 * Dashboard de alertas de ruptura e compra inteligente.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  WandSparkles, RefreshCw, FilePlus,
  Search, AlertTriangle, ShieldAlert, Activity, ShoppingCart, Loader2, Settings2
} from 'lucide-react'
import ErpFloatingMenu from '../../../../components/ERP/ErpFloatingMenu/ErpFloatingMenu'
import ErpPeriodDropdown from '../../../../components/ERP/ErpPeriodDropdown/ErpPeriodDropdown'
import BrandSearchSelect from '../../../../components/ERP/Purchases/BrandSearchSelect/BrandSearchSelect'
import RiskFiltersPanel from '../../../../components/ERP/Purchases/RiskFiltersPanel/RiskFiltersPanel'
import { PURCHASES_QUICK_MENU_ITEMS } from '../../../../config/purchasesQuickMenu'
import KPICard from '../../../../components/ERP/FinanceDashboard/KPICard/KPICard'
import StockRiskAlertsTable from '../../../../components/ERP/Purchases/StockRiskAlertsTable/StockRiskAlertsTable'
import PurchaseTargetConfigModal from '../../../../components/ERP/Purchases/PurchaseTargetConfigModal/PurchaseTargetConfigModal'
import StockoutProjectionChart from '../../../../components/ERP/Purchases/StockoutProjectionChart/StockoutProjectionChart'
import MonthlyConsumptionBarChart from '../../../../components/ERP/Purchases/MonthlyConsumptionBarChart/MonthlyConsumptionBarChart'
import SeasonalAdjustmentsPanel from '../../../../components/ERP/Purchases/SeasonalAdjustmentsPanel/SeasonalAdjustmentsPanel'
import StockoutAiAnalysisModal from '../../../../components/ERP/Purchases/StockoutAiAnalysisModal/StockoutAiAnalysisModal'
import DonutChart from '../../../../components/ERP/FinanceDashboard/DonutChart/DonutChart'
import { compraInteligenteService } from '../../../../services/compraInteligenteService'
import { useToast } from '../../../../hooks/useToast'
import { Toast } from '../../../../components'
import './CompraInteligenteDashboardPage.css'

const PLACEHOLDER_KPIS = [
  { label: 'Críticos', value: '—', change: '—', up: false, icon: AlertTriangle, color: 'text-rose-500', bg: 'bg-rose-50' },
  { label: 'Risco Alto', value: '—', change: '—', up: false, icon: ShieldAlert, color: 'text-amber-500', bg: 'bg-amber-50' },
  { label: 'Risco Médio', value: '—', change: '—', up: false, icon: Activity, color: 'text-blue-500', bg: 'bg-blue-50' },
  { label: 'Abaixo do Mínimo', value: '—', change: '—', up: false, icon: ShoppingCart, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  { label: 'Valor Sugerido', value: '—', change: '—', up: true, icon: WandSparkles, color: 'text-emerald-500', bg: 'bg-emerald-50' }
]

const RISK_FILTER_OPTIONS = [
  { value: '', label: 'Todos os riscos' },
  { value: 'critical', label: 'Crítico' },
  { value: 'high', label: 'Alto' },
  { value: 'medium', label: 'Médio' }
]

function CompraInteligenteDashboardPage() {
  const navigate = useNavigate()
  const { toast, showToast, hideToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [initialLoading, setInitialLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [alertsLoading, setAlertsLoading] = useState(false)
  const [dashboardData, setDashboardData] = useState(null)
  const [alertsData, setAlertsData] = useState(null)
  const [brands, setBrands] = useState([])
  const [configModalOpen, setConfigModalOpen] = useState(false)
  const [error, setError] = useState(null)
  const [projectionData, setProjectionData] = useState(null)
  const [projectionLoading, setProjectionLoading] = useState(false)
  const [consumptionData, setConsumptionData] = useState(null)
  const [consumptionLoading, setConsumptionLoading] = useState(false)
  const [seasonalData, setSeasonalData] = useState(null)
  const [seasonalLoading, setSeasonalLoading] = useState(false)
  const [seasonalApplying, setSeasonalApplying] = useState(false)
  const [riskDistribution, setRiskDistribution] = useState([])
  const [selectedAlertIds, setSelectedAlertIds] = useState([])
  const [aiModalOpen, setAiModalOpen] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiAnalyses, setAiAnalyses] = useState([])
  const [aiAnalysisMap, setAiAnalysisMap] = useState({})

  const [brandFilter, setBrandFilter] = useState(searchParams.get('brand') || '')
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get('supplierId') || '')
  const [riskFilter, setRiskFilter] = useState(searchParams.get('riskLevel') || '')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const hasDisplayedDataRef = useRef(false)

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)

  const loadDashboard = useCallback(async () => {
    if (hasDisplayedDataRef.current) setRefreshing(true)
    else setInitialLoading(true)
    setError(null)

    try {
      const response = await compraInteligenteService.getDashboard()
      if (response.success) {
        setDashboardData(response.data)
        hasDisplayedDataRef.current = true
      } else {
        setError('Erro ao carregar dashboard')
      }
    } catch (err) {
      console.error(err)
      setError('Erro ao carregar dados. Tente novamente.')
    } finally {
      setInitialLoading(false)
      setRefreshing(false)
    }
  }, [])

  const loadBrands = useCallback(async () => {
    try {
      const response = await compraInteligenteService.getBrands()
      if (response.success) setBrands(response.data || [])
    } catch (err) {
      console.error('Erro ao carregar marcas:', err)
    }
  }, [])

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true)
    try {
      const response = await compraInteligenteService.getAlerts({
        brand: brandFilter || undefined,
        supplierId: supplierFilter || undefined,
        riskLevel: riskFilter || undefined,
        search: search.trim() || undefined,
        page,
        limit: 20
      })
      if (response.success) setAlertsData(response.data)
    } catch (err) {
      console.error('Erro ao carregar alertas:', err)
    } finally {
      setAlertsLoading(false)
    }
  }, [brandFilter, supplierFilter, riskFilter, search, page])

  const loadProjection = useCallback(async () => {
    setProjectionLoading(true)
    try {
      const response = await compraInteligenteService.getProjection({
        brand: brandFilter || undefined,
        limit: 12
      })
      if (response.success) setProjectionData(response.data)
    } catch (err) {
      console.error('Erro ao carregar projeção:', err)
    } finally {
      setProjectionLoading(false)
    }
  }, [brandFilter])

  const loadRiskDistribution = useCallback(async () => {
    try {
      const response = await compraInteligenteService.getRiskDistribution()
      if (response.success) setRiskDistribution(response.data || [])
    } catch (err) {
      console.error('Erro ao carregar distribuição de risco:', err)
    }
  }, [])

  const loadBrandAnalytics = useCallback(async () => {
    if (!brandFilter) {
      setConsumptionData(null)
      setSeasonalData(null)
      return
    }

    setConsumptionLoading(true)
    setSeasonalLoading(true)
    try {
      const [consumptionRes, seasonalRes] = await Promise.all([
        compraInteligenteService.getMonthlyConsumption(brandFilter),
        compraInteligenteService.getSeasonal(brandFilter)
      ])
      if (consumptionRes.success) setConsumptionData(consumptionRes.data)
      if (seasonalRes.success) setSeasonalData(seasonalRes.data)
    } catch (err) {
      console.error('Erro ao carregar análise da marca:', err)
    } finally {
      setConsumptionLoading(false)
      setSeasonalLoading(false)
    }
  }, [brandFilter])

  useEffect(() => {
    loadDashboard()
    loadBrands()
    loadRiskDistribution()
  }, [loadDashboard, loadBrands, loadRiskDistribution])

  useEffect(() => {
    loadAlerts()
    loadProjection()
    loadBrandAnalytics()
  }, [loadAlerts, loadProjection, loadBrandAnalytics])

  useEffect(() => {
    const params = {}
    if (brandFilter) params.brand = brandFilter
    if (supplierFilter) params.supplierId = supplierFilter
    if (riskFilter) params.riskLevel = riskFilter
    setSearchParams(params, { replace: true })
  }, [brandFilter, supplierFilter, riskFilter, setSearchParams])

  const kpiData = useMemo(() => {
    const kpis = dashboardData?.kpis
    if (!kpis) return PLACEHOLDER_KPIS

    return [
      {
        label: 'Críticos',
        value: String(kpis.criticalCount),
        change: 'sem estoque disponível',
        up: false,
        icon: AlertTriangle,
        color: 'text-rose-500',
        bg: 'bg-rose-50'
      },
      {
        label: 'Risco Alto',
        value: String(kpis.highCount),
        change: 'abaixo do mínimo',
        up: false,
        icon: ShieldAlert,
        color: 'text-amber-500',
        bg: 'bg-amber-50'
      },
      {
        label: 'Risco Médio',
        value: String(kpis.mediumCount),
        change: 'próximo do mínimo',
        up: false,
        icon: Activity,
        color: 'text-blue-500',
        bg: 'bg-blue-50'
      },
      {
        label: 'Abaixo do Mínimo',
        value: String(kpis.belowMinimumCount),
        change: 'críticos + alto',
        up: false,
        icon: ShoppingCart,
        color: 'text-indigo-500',
        bg: 'bg-indigo-50'
      },
      {
        label: 'Valor Sugerido',
        value: formatCurrency(kpis.suggestedOrdersValue),
        change: 'reposição estimada',
        up: true,
        icon: WandSparkles,
        color: 'text-emerald-500',
        bg: 'bg-emerald-50'
      }
    ]
  }, [dashboardData])

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    setPage(1)
    loadAlerts()
  }

  const handleCreateDraftsFromAlerts = () => {
    if (!selectedAlertIds.length) {
      showToast('Selecione ao menos um produto na tabela', 'warning')
      return
    }

    navigate('/erp/purchases/intelligence/create-drafts', {
      state: {
        productIds: selectedAlertIds,
        aiAnalysis: aiAnalysisMap
      }
    })
  }

  const handleAnalyzeAi = async (productIds = []) => {
    const ids = [...new Set((productIds || []).map((id) => Number(id)).filter(Boolean))]
    if (!ids.length) {
      showToast('Selecione ao menos um produto para análise', 'warning')
      return
    }
    if (ids.length > 20) {
      showToast('Máximo de 20 produtos por análise IA', 'warning')
      return
    }

    setAiModalOpen(true)
    setAiLoading(true)
    setAiAnalyses([])

    try {
      const response = await compraInteligenteService.analyzeAi(ids)
      if (response.success) {
        setAiAnalyses(response.data?.analyses || [])
        if (!response.data?.analyses?.length) {
          showToast('Nenhuma análise retornada pela IA', 'warning')
        }
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Erro na análise IA'
      showToast(message, 'error')
      setAiModalOpen(false)
    } finally {
      setAiLoading(false)
    }
  }

  const handleApplyAiAnalyses = (payload = []) => {
    const nextMap = { ...aiAnalysisMap }
    payload.forEach((item) => {
      nextMap[item.productId] = {
        suggestedQty: item.suggestedQty,
        reason: item.reason
      }
    })
    setAiAnalysisMap(nextMap)
    setAiModalOpen(false)
    showToast(
      `Quantidades IA aplicadas para ${payload.length} produto(s). Serão usadas ao gerar rascunhos.`,
      'success'
    )
  }

  const handleApplySeasonal = async (productCodes = null) => {
    if (!brandFilter) return
    setSeasonalApplying(true)
    try {
      const response = await compraInteligenteService.applySeasonal(brandFilter, productCodes)
      if (response.success) {
        showToast(response.message || 'Ajustes sazonais aplicados', 'success')
        loadBrandAnalytics()
        loadDashboard()
        loadAlerts()
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Erro ao aplicar ajustes sazonais', 'error')
    } finally {
      setSeasonalApplying(false)
    }
  }

  const handleClearFilters = () => {
    setBrandFilter('')
    setSupplierFilter('')
    setPage(1)
  }

  const handleSupplierFilterChange = (value) => {
    setSupplierFilter(value)
    setPage(1)
  }

  const handleBrandFilterChange = (value) => {
    setBrandFilter(value)
    setPage(1)
  }

  if (initialLoading && !hasDisplayedDataRef.current) {
    return (
      <div className="compra-inteligente compra-inteligente-loading">
        <Loader2 size={32} className="compra-inteligente-spinner" />
        <span>Carregando Compra Inteligente...</span>
      </div>
    )
  }

  return (
    <div className={`compra-inteligente ${refreshing ? 'is-refreshing' : ''}`}>
      <Toast toast={toast} onClose={hideToast} />
      <header className="compra-inteligente-header">
        <div className="compra-inteligente-header-main">
          <h1 className="compra-inteligente-title">
            <WandSparkles size={28} />
            Compra Inteligente
          </h1>
          <p className="compra-inteligente-subtitle">
            Alertas de ruptura por marca e fornecedor, com reposição baseada no estoque mínimo
            {dashboardData?.purchaseTargetMultiplier > 1
              ? ` (meta ${dashboardData.purchaseTargetMultiplier}×)`
              : ''}
          </p>
        </div>
        <div className="compra-inteligente-header-actions">
          <button
            type="button"
            className="compra-inteligente-reload-btn"
            onClick={() => {
              loadDashboard()
              loadAlerts()
              loadProjection()
              loadBrandAnalytics()
              loadRiskDistribution()
            }}
            disabled={refreshing}
            title="Atualizar"
          >
            <RefreshCw size={18} className={refreshing ? 'is-spinning' : ''} />
          </button>
          <button
            type="button"
            className="compra-inteligente-secondary-btn"
            onClick={() => setConfigModalOpen(true)}
            title="Configurar meta de compra"
          >
            <Settings2 size={16} />
            Meta de compra
          </button>
          <button
            type="button"
            className="compra-inteligente-primary-btn"
            onClick={() => navigate('/erp/purchases/new-request')}
          >
            <FilePlus size={16} />
            Nova Requisição
          </button>
        </div>
      </header>

      {error && (
        <div className="compra-inteligente-error-banner">
          {error}
          <button type="button" onClick={loadDashboard}>Tentar novamente</button>
        </div>
      )}

      <ErpFloatingMenu items={PURCHASES_QUICK_MENU_ITEMS} />

      <section className="compra-inteligente-kpi-grid">
        {kpiData.map((kpi) => (
          <KPICard key={kpi.label} {...kpi} />
        ))}
      </section>

      <section className="compra-inteligente-analytics-row">
        <StockoutProjectionChart
          items={projectionData?.items || []}
          loading={projectionLoading}
        />
        <div className="compra-inteligente-analytics-side">
          {riskDistribution.length > 0 && (
            <div className="compra-inteligente-donut-card">
              <h2 className="compra-inteligente-card-title">Distribuição de risco</h2>
              <DonutChart data={riskDistribution} legendMode="floating" />
            </div>
          )}
          <MonthlyConsumptionBarChart
            data={consumptionData}
            loading={consumptionLoading}
          />
        </div>
      </section>

      {brandFilter && (
        <section className="compra-inteligente-seasonal-section">
          <SeasonalAdjustmentsPanel
            data={seasonalData}
            loading={seasonalLoading}
            applying={seasonalApplying}
            onApply={(codes) => handleApplySeasonal(codes)}
            onApplyAll={() => handleApplySeasonal()}
          />
        </section>
      )}

      <RiskFiltersPanel
        suppliers={dashboardData?.riskBySupplier || []}
        brands={dashboardData?.riskByBrand || []}
        supplierFilter={supplierFilter}
        brandFilter={brandFilter}
        onSupplierChange={handleSupplierFilterChange}
        onBrandChange={handleBrandFilterChange}
        onClearFilters={handleClearFilters}
      />

      <section className="compra-inteligente-alerts-section">
        <div className="compra-inteligente-alerts-header">
          <div className="compra-inteligente-alerts-title-row">
            <h2 className="compra-inteligente-card-title">Alertas de ruptura</h2>
            {Object.keys(aiAnalysisMap).length > 0 && (
              <span className="compra-inteligente-ai-active-badge">
                IA ativa · {Object.keys(aiAnalysisMap).length} produto(s)
              </span>
            )}
          </div>
          <form className="compra-inteligente-filters" onSubmit={handleSearchSubmit}>
            <div className="compra-inteligente-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Buscar produto, SKU ou marca..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="compra-inteligente-filter-brand">
              <BrandSearchSelect
                value={brandFilter}
                brands={brands}
                onChange={(brand) => handleBrandFilterChange(brand)}
                label=""
                placeholder="Todas as marcas"
                modalTitle="Filtrar por marca"
                modalSubtitle="Busque ou role a lista para selecionar uma marca"
              />
            </div>
            <ErpPeriodDropdown
              label="Risco:"
              value={riskFilter}
              options={RISK_FILTER_OPTIONS}
              onChange={(value) => { setRiskFilter(value); setPage(1) }}
            />
            <button type="submit" className="compra-inteligente-filter-btn">Filtrar</button>
            <button
              type="button"
              className="compra-inteligente-draft-btn"
              disabled={selectedAlertIds.length === 0}
              onClick={handleCreateDraftsFromAlerts}
            >
              <FilePlus size={14} />
              Criar rascunhos
              {selectedAlertIds.length > 0 && ` (${selectedAlertIds.length})`}
            </button>
            <button
              type="button"
              className="compra-inteligente-ai-btn"
              disabled={selectedAlertIds.length === 0 || aiLoading}
              onClick={() => handleAnalyzeAi(selectedAlertIds)}
            >
              {aiLoading ? <Loader2 size={14} className="compra-inteligente-spinner" /> : <WandSparkles size={14} />}
              Analisar selecionados
              {selectedAlertIds.length > 0 && ` (${selectedAlertIds.length})`}
            </button>
          </form>
        </div>

        <StockRiskAlertsTable
          items={alertsData?.items || []}
          loading={alertsLoading}
          pagination={alertsData?.pagination}
          onPageChange={setPage}
          selectedIds={selectedAlertIds}
          onSelectionChange={setSelectedAlertIds}
          onAnalyzeProduct={handleAnalyzeAi}
        />
      </section>

      <StockoutAiAnalysisModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        loading={aiLoading}
        analyses={aiAnalyses}
        onApply={handleApplyAiAnalyses}
      />

      <PurchaseTargetConfigModal
        isOpen={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        onSaved={(config) => {
          const multiplier = config?.purchaseTargetMultiplier ?? 1
          showToast(`Meta de compra salva: ${multiplier}× estoque mínimo`, 'success')
          loadDashboard()
          loadAlerts()
          loadProjection()
          loadRiskDistribution()
        }}
      />
    </div>
  )
}

export default CompraInteligenteDashboardPage
